import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorIcon from "@mui/icons-material/Error";
import GroupsIcon from "@mui/icons-material/Groups";

import Navbar from "./AdminNav";
import Footer from "../Footer";
import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";

const BulkEmailHistory = () => {
  /*
   * ---------------------------------------------------------
   * DATE HELPERS
   * ---------------------------------------------------------
   */

  const getToday = () => {
    const date = new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getDateMonthsAgo = (months) => {
    const date = new Date();
    date.setMonth(date.getMonth() - months);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  /*
   * ---------------------------------------------------------
   * STATE
   * ---------------------------------------------------------
   */

  const [fromDate, setFromDate] = useState(getDateMonthsAgo(1));
  const [toDate, setToDate] = useState(getToday());

  const [emailBatches, setEmailBatches] = useState([]);

  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /*
   * ---------------------------------------------------------
   * TOAST
   * ---------------------------------------------------------
   */

  const showToast = (message, severity = "success") => {
    setToast({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({
      ...prev,
      open: false,
    }));
  };

  /*
   * ---------------------------------------------------------
   * DATE FORMATTER
   * ---------------------------------------------------------
   */

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /*
   * ---------------------------------------------------------
   * FETCH EMAIL BATCHES
   * ---------------------------------------------------------
   */

  const fetchEmailBatches = useCallback(async () => {
    if (!fromDate || !toDate) {
      showToast("Please select both dates", "warning");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      showToast("From date cannot be after To date", "warning");
      return;
    }

    setLoading(true);

    try {
      const res = await api.get(
        ENDPOINTS.getBulkEmailBatches({
          fromDate,
          toDate,
        })
      );

      const responseData = res.data;

      if (
        responseData?.status?.toLowerCase() === "success" &&
        Array.isArray(responseData?.data)
      ) {
        setEmailBatches(responseData.data);
        setPage(0);
      } else {
        setEmailBatches([]);
        showToast("No email batches found", "info");
      }
    } catch (err) {
      console.error("Error fetching email batches:", err);

      setEmailBatches([]);

      showToast("Failed to load sent emails", "error");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  /*
   * ---------------------------------------------------------
   * INITIAL LOAD
   * ---------------------------------------------------------
   */

  useEffect(() => {
    fetchEmailBatches();
  }, [fetchEmailBatches]);

  /*
   * ---------------------------------------------------------
   * SEARCH
   * ---------------------------------------------------------
   */

  const handleSearch = () => {
    fetchEmailBatches();
  };

  /*
   * ---------------------------------------------------------
   * RESET
   * ---------------------------------------------------------
   */

  const handleReset = () => {
    const defaultFromDate = getDateMonthsAgo(1);
    const defaultToDate = getToday();

    setFromDate(defaultFromDate);
    setToDate(defaultToDate);
    setPage(0);
  };

  /*
   * ---------------------------------------------------------
   * PAGINATION
   * ---------------------------------------------------------
   */

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /*
   * ---------------------------------------------------------
   * STATUS CHIP
   * ---------------------------------------------------------
   */

  const renderStatus = (batch) => {
    const {
      totalEmails = 0,
      sentCount = 0,
      pendingCount = 0,
      failedCount = 0,
    } = batch;

    if (failedCount > 0) {
      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          <Chip
            icon={<GroupsIcon />}
            label={`Total: ${totalEmails}`}
            size="small"
            variant="outlined"
          />

          <Chip
            icon={<CheckCircleIcon />}
            label={`Sent: ${sentCount}`}
            size="small"
            color="success"
            variant="outlined"
          />

          {pendingCount > 0 && (
            <Chip
              icon={<ScheduleIcon />}
              label={`Pending: ${pendingCount}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}

          <Chip
            icon={<ErrorIcon />}
            label={`Failed: ${failedCount}`}
            size="small"
            color="error"
            variant="outlined"
          />
        </Stack>
      );
    }

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        <Chip
          icon={<GroupsIcon />}
          label={`Total: ${totalEmails}`}
          size="small"
          variant="outlined"
        />

        <Chip
          icon={<CheckCircleIcon />}
          label={`Sent: ${sentCount}`}
          size="small"
          color="success"
          variant="outlined"
        />

        {pendingCount > 0 && (
          <Chip
            icon={<ScheduleIcon />}
            label={`Pending: ${pendingCount}`}
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
      </Stack>
    );
  };

  /*
   * ---------------------------------------------------------
   * BATCH TYPE CHIP
   * ---------------------------------------------------------
   */

  const renderBatchType = (batchType) => {
    if (!batchType) return "-";

    return (
      <Chip
        label={batchType}
        size="small"
        color={batchType === "RoleAlert" ? "secondary" : "primary"}
        variant="outlined"
      />
    );
  };

  /*
   * ---------------------------------------------------------
   * PAGINATED DATA
   * ---------------------------------------------------------
   */

  const paginatedBatches = emailBatches.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /*
   * ---------------------------------------------------------
   * SUMMARY
   * ---------------------------------------------------------
   */

  const totalBatches = emailBatches.length;

  const totalEmails = emailBatches.reduce(
    (sum, batch) => sum + Number(batch.totalEmails || 0),
    0
  );

  const totalSent = emailBatches.reduce(
    (sum, batch) => sum + Number(batch.sentCount || 0),
    0
  );

  const totalPending = emailBatches.reduce(
    (sum, batch) => sum + Number(batch.pendingCount || 0),
    0
  );

  const totalFailed = emailBatches.reduce(
    (sum, batch) => sum + Number(batch.failedCount || 0),
    0
  );

  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <>
      <Navbar />

      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          p: 3,
        }}
      >
        {/* ---------------------------------------------------
            PAGE TITLE
        --------------------------------------------------- */}

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          mb={3}
        >
          <EmailIcon color="primary" />

          <Typography variant="h4">
            Emails Sent
          </Typography>
        </Stack>

        {/* ---------------------------------------------------
            FILTERS
        --------------------------------------------------- */}

        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
          }}
        >
          <Typography variant="h6" mb={2}>
            Search Email History
          </Typography>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
          >
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              sx={{
                maxWidth: {
                  sm: 220,
                },
              }}
            />

            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              sx={{
                maxWidth: {
                  sm: 220,
                },
              }}
            />

            <Button
              variant="contained"
              size="large"
              startIcon={
                loading ? (
                  <CircularProgress
                    size={18}
                    color="inherit"
                  />
                ) : (
                  <SearchIcon />
                )
              }
              onClick={handleSearch}
              disabled={loading}
              sx={{
                minWidth: 130,
              }}
            >
              {loading ? "Loading..." : "Search"}
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
          </Stack>
        </Paper>

        {/* ---------------------------------------------------
            SUMMARY
        --------------------------------------------------- */}

        {!loading && emailBatches.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 2,
              mb: 3,
            }}
          >
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={{
                xs: 1.5,
                sm: 3,
              }}
              divider={
                <Divider
                  orientation="vertical"
                  flexItem
                />
              }
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Email Batches
                </Typography>

                <Typography variant="h6">
                  {totalBatches}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Total Emails
                </Typography>

                <Typography variant="h6">
                  {totalEmails}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Sent
                </Typography>

                <Typography
                  variant="h6"
                  color="success.main"
                >
                  {totalSent}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Pending
                </Typography>

                <Typography
                  variant="h6"
                  color="warning.main"
                >
                  {totalPending}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Failed
                </Typography>

                <Typography
                  variant="h6"
                  color="error.main"
                >
                  {totalFailed}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {/* ---------------------------------------------------
            EMAIL TABLE
        --------------------------------------------------- */}

        <Paper elevation={2}>
          <TableContainer
            sx={{
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 1050,
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Subject
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Type
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Notice
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Queued At
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Delivery Status
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Last Sent At
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        py: 6,
                      }}
                    >
                      <CircularProgress />

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 2,
                        }}
                      >
                        Loading sent emails...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedBatches.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        py: 6,
                      }}
                    >
                      <EmailIcon
                        sx={{
                          fontSize: 45,
                          color: "text.disabled",
                          mb: 1,
                        }}
                      />

                      <Typography
                        variant="h6"
                        color="text.secondary"
                      >
                        No emails found
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        No email batches were found for the
                        selected date range.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedBatches.map((batch, index) => (
                    <TableRow
                      key={`${batch.queuedAt}-${index}`}
                      hover
                    >
                      {/* SUBJECT */}

                      <TableCell
                        sx={{
                          maxWidth: 300,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {batch.subject || "-"}
                        </Typography>
                      </TableCell>

                      {/* BATCH TYPE */}

                      <TableCell>
                        {renderBatchType(batch.batchType)}
                      </TableCell>

                      {/* NOTICE */}

                      <TableCell
                        sx={{
                          maxWidth: 220,
                        }}
                      >
                        {batch.noticeTitle ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                wordBreak: "break-word",
                              }}
                            >
                              {batch.noticeTitle}
                            </Typography>

                            {batch.noticeId && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                ID: {batch.noticeId}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* QUEUED AT */}

                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Typography variant="body2">
                          {formatDateTime(batch.queuedAt)}
                        </Typography>
                      </TableCell>

                      {/* STATUS */}

                      <TableCell
                        sx={{
                          minWidth: 250,
                        }}
                      >
                        {renderStatus(batch)}
                      </TableCell>

                      {/* LAST SENT */}

                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Typography variant="body2">
                          {formatDateTime(batch.lastSentAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && emailBatches.length > 0 && (
            <TablePagination
              component="div"
              count={emailBatches.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          )}
        </Paper>
      </Box>

      {/* -----------------------------------------------------
          TOAST
      ----------------------------------------------------- */}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={toast.severity}
          onClose={handleCloseToast}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Footer />
    </>
  );
};

export default BulkEmailHistory;